import type { ApiResponse } from '@/api/transport';
import { t } from '@/i18n/i18n';
import type { VideoListResponse, VideoReference } from '@/api/types';
import { mockFilterOptions, mockPlans, mockVideos } from '@/mocks/data';
import { route } from './handlers';
import { FREE_CANVASES, canvasIdsForVideo, getState, patchState } from './state';

/** Free-tier в mock — 40 первых результатов, как в прототипе (на проде FREE_VIDEO_LIMIT = 100). */
const FREE_TIER_CAP = 40;
const SHOW_MORE_THRESHOLD = 5;

const ok = (body: unknown, status = 200): ApiResponse => ({ status, body });
const fail = (status: number, message: string, error?: string): ApiResponse => ({
  status,
  body: error ? { message, error } : { message },
});

const FEATURE_FLAGS = [
  'has_visual_effects',
  'has_3d',
  'has_animations',
  'has_typography',
  'has_sound_design',
  'has_ai',
  'has_motion_graphics',
] as const;

function list(query: URLSearchParams, key: string): string[] {
  return [...query.getAll(`${key}[]`), ...query.getAll(key)].filter(Boolean);
}

function withCanvasState(video: VideoReference): VideoReference {
  const ids = canvasIdsForVideo(video.id);
  return { ...video, saved_canvas_ids: ids, is_saved: ids.length > 0 };
}

/**
 * Поиск v3 в mock-режиме (ADR-009) — чтобы зоны и подсказки было видно без бэкенда:
 *  - любой текстовый запрос отдаёт `meta.zones` (первые 60% совпадений — «точные»);
 *  - запрос с префиксом `near ` ищет по остатку и кладёт всё в «близкие» — баннер «точных нет»;
 *  - обрывки из MOCK_COMPLETIONS отдают `interpreted_as`;
 *  - запрос, равный имени категории, отдаёт `category_hint`; пустая выдача — `did_you_mean`;
 *  - запросы из MOCK_TRANSLATIONS ведут себя как не-английские на бэкенде: фаза `typing` перевод не зовёт
 *    и отвечает пусто, полная фаза ищет по переводу и отдаёт `interpreted_as`.
 */
const MOCK_NEAR_PREFIX = 'near ';
const MOCK_COMPLETIONS: Record<string, string> = { transi: 'transition', weddi: 'wedding' };
const MOCK_DID_YOU_MEAN = 'product';
const MOCK_TRANSLATIONS: Record<string, string> = { переход: 'transition', свадьба: 'wedding' };

function searchText(query: URLSearchParams): string {
  const raw = (query.get('search') ?? '').trim().toLowerCase();
  const translated = MOCK_TRANSLATIONS[raw];
  if (translated) return query.get('search_mode') === 'typing' ? raw : translated;
  return raw.startsWith(MOCK_NEAR_PREFIX) ? raw.slice(MOCK_NEAR_PREFIX.length).trim() : raw;
}

function matches(video: VideoReference, query: URLSearchParams): boolean {
  const search = searchText(query);
  if (search.length >= 2) {
    const haystack = [
      video.title,
      ...video.categories.map((c) => c.name),
      ...video.transition_types.map((t) => t.name),
      video.hook?.name ?? '',
      video.pacing ?? '',
    ]
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(search)) return false;
  }
  const categoryIds = list(query, 'category_ids').map(Number);
  if (categoryIds.length && !video.categories.some((c) => categoryIds.includes(c.id))) return false;
  const transitionIds = list(query, 'transition_type_ids').map(Number);
  if (transitionIds.length && !video.transition_types.some((t) => transitionIds.includes(t.id)))
    return false;
  const hookIds = list(query, 'hook_ids').map(Number);
  if (hookIds.length && !(video.hook && hookIds.includes(video.hook.id))) return false;
  const pacing = list(query, 'pacing');
  if (pacing.length && !pacing.includes(video.pacing ?? '')) return false;
  const level = list(query, 'production_level');
  if (level.length && !level.includes(video.production_level ?? '')) return false;
  const orientation = list(query, 'orientation');
  if (orientation.length && !orientation.includes(video.orientation ?? '')) return false;
  const platforms = list(query, 'tutorial_platforms');
  if (platforms.length) {
    const has = video.tutorials.some((t) =>
      platforms.some(
        (p) => !!t[`${p.toLowerCase()}_url` as 'fc_url' | 'pp_url' | 'dr_url' | 'cc_url'],
      ),
    );
    if (!has) return false;
  }
  if (query.get('has_tutorial') === 'true' && video.tutorials.length === 0) return false;
  for (const flag of FEATURE_FLAGS) {
    if (query.get(flag) === 'true' && !video[flag]) return false;
  }
  return true;
}

route('GET', '/video-references', ({ req, query }) => {
  const isFreeTier = !getState().fullAccess;
  const perPage = Math.max(1, Math.min(60, Number(query.get('per_page')) || 20));
  const page = Math.max(1, Number(query.get('page')) || 1);
  const all = mockVideos.filter((v) => matches(v, query));
  const realTotal = all.length;
  const capped = isFreeTier ? Math.min(realTotal, FREE_TIER_CAP) : realTotal;
  const lastPage = Math.max(1, Math.ceil(capped / perPage));
  const items = all
    .slice((page - 1) * perPage, Math.min(page * perPage, capped))
    .map(withCanvasState);

  const search = (query.get('search') ?? '').trim();
  const body: VideoListResponse = {
    data: items,
    meta: {
      current_page: page,
      last_page: lastPage,
      per_page: perPage,
      total: capped,
      total_uncapped: realTotal,
    },
  };
  if (search.length >= 2) {
    const fromHeader = Number(req.headers['X-Search-Event-Id']);
    if (page > 1 && fromHeader) {
      body.meta.search_event_id = fromHeader;
    } else if (query.get('search_mode') !== 'typing') {
      // Набор на лету поиском не считается — события у него нет, как на бэкенде.
      const id = getState().nextSearchEventId;
      patchState({ nextSearchEventId: id + 1 });
      body.meta.search_event_id = id;
    }

    const lowered = search.toLowerCase();
    const exact = lowered.startsWith(MOCK_NEAR_PREFIX) ? 0 : Math.ceil(realTotal * 0.6);
    body.meta.zones = { exact, close: realTotal - exact, nearest: 0 };
    body.meta.tier1_count = exact;
    if (MOCK_COMPLETIONS[lowered]) body.meta.interpreted_as = MOCK_COMPLETIONS[lowered];
    if (MOCK_TRANSLATIONS[lowered] && query.get('search_mode') !== 'typing')
      body.meta.interpreted_as = MOCK_TRANSLATIONS[lowered];
    if (realTotal === 0) body.meta.did_you_mean = MOCK_DID_YOU_MEAN;
    const category = all
      .flatMap((video) => video.categories)
      .find((c) => c.name.toLowerCase() === searchText(query));
    if (category) body.meta.category_hint = { id: category.id, name: category.name };
  }
  if (isFreeTier) {
    body.subscription_required = true;
    body.free_tier_cap = FREE_TIER_CAP;
    body.show_more_threshold = SHOW_MORE_THRESHOLD;
  }
  return ok(body);
});

route('GET', '/video-references/:id', ({ params }) => {
  const video = mockVideos.find((v) => v.id === Number(params['id']));
  return video ? ok({ data: withCanvasState(video) }) : fail(404, 'Not found');
});

route('GET', '/filter-options', () => ok(mockFilterOptions));

route('POST', '/search/click', () => ok({ ok: true }, 202));

route('GET', '/subscription-plans', () => ok({ data: mockPlans }));

// ——— Канвасы (B1) ———
function canvasWithCounts(canvas: (typeof mockVideosCanvasType)[number]) {
  const videos = getState().canvasVideos[String(canvas.id)] ?? [];
  return {
    ...canvas,
    nodes_count: canvas.nodes_count + Math.max(0, videos.length - initialCount(canvas.id)),
  };
}
const mockVideosCanvasType = getState().canvases;
function initialCount(canvasId: number): number {
  return canvasId === 1 ? 2 : canvasId === 2 ? 1 : 0;
}

route('GET', '/canvases', () => ok({ data: getState().canvases.map(canvasWithCounts) }));

route('POST', '/canvases', ({ body }) => {
  const s = getState();
  // Бесплатный тариф: один канвас (доска закладок «Saved» не считается).
  if (!s.fullAccess && s.canvases.filter((c) => c.name !== 'Saved').length >= FREE_CANVASES)
    return fail(403, 'Free plan includes one canvas', 'canvas_limit');
  const id = s.nextCanvasId;
  const name =
    typeof body['name'] === 'string' && body['name'].trim()
      ? body['name'].trim()
      : t('library.untitled', { n: s.canvases.length + 1 });
  const canvas = {
    id,
    name,
    description: null,
    is_default: s.canvases.length === 0,
    nodes_count: 0,
    cover_url: null,
    participants: [
      {
        id: 0,
        user_id: 1,
        name: 'Alex Kim',
        initials: 'AK',
        role: 'owner' as const,
        via: 'invite' as const,
        status: 'accepted' as const,
        email: null,
      },
    ],
    share: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  patchState({
    canvases: [canvas, ...s.canvases],
    canvasVideos: { ...s.canvasVideos, [id]: [] },
    nextCanvasId: id + 1,
  });
  return ok({ data: canvas }, 201);
});

route('PATCH', '/canvases/:id', ({ params, body }) => {
  const id = Number(params['id']);
  const name = typeof body['name'] === 'string' ? body['name'] : undefined;
  const canvases = getState().canvases.map((c) =>
    c.id === id
      ? { ...c, ...(name !== undefined ? { name } : {}), updated_at: new Date().toISOString() }
      : c,
  );
  patchState({ canvases });
  const canvas = canvases.find((c) => c.id === id);
  return canvas ? ok({ data: canvasWithCounts(canvas) }) : fail(404, 'Not found');
});

route('DELETE', '/canvases/:id', ({ params }) => {
  const id = Number(params['id']);
  const s = getState();
  const rest = { ...s.canvasVideos };
  delete rest[String(id)];
  patchState({ canvases: s.canvases.filter((c) => c.id !== id), canvasVideos: rest });
  return ok({ ok: true });
});

route('POST', '/canvases/:id/set-default', ({ params }) => {
  const id = Number(params['id']);
  const canvases = getState().canvases.map((c) => ({ ...c, is_default: c.id === id }));
  patchState({ canvases });
  const canvas = canvases.find((c) => c.id === id);
  return canvas ? ok({ data: canvasWithCounts(canvas) }) : fail(404, 'Not found');
});

route('POST', '/canvases/:id/videos', ({ params, body }) => {
  const id = Number(params['id']);
  const s = getState();
  const canvas = s.canvases.find((c) => c.id === id);
  if (!canvas) return fail(404, 'Not found');
  const raw = body['video_reference_ids'];
  const requested = (Array.isArray(raw) ? raw : [raw])
    .map(Number)
    .filter((n) => Number.isFinite(n));
  const current = s.canvasVideos[String(id)] ?? [];
  const added = requested.filter((v) => !current.includes(v));
  const already = requested.filter((v) => current.includes(v));
  const canvasVideos = { ...s.canvasVideos, [id]: [...current, ...added] };
  const canvases = s.canvases.map((c) =>
    c.id === id
      ? {
          ...c,
          updated_at: new Date().toISOString(),
          cover_url:
            c.cover_url ?? mockVideos.find((v) => v.id === added[0])?.extra?.thumbnail_url ?? null,
        }
      : c,
  );
  patchState({ canvasVideos, canvases });
  return ok(
    {
      data: canvasWithCounts(canvases.find((c) => c.id === id)!),
      added_ids: added,
      already_present_ids: already,
    },
    201,
  );
});

route('DELETE', '/canvases/:id/videos/:videoId', ({ params }) => {
  const id = Number(params['id']);
  const videoId = Number(params['videoId']);
  const s = getState();
  const canvas = s.canvases.find((c) => c.id === id);
  if (!canvas) return fail(404, 'Not found');
  const canvasVideos = {
    ...s.canvasVideos,
    [id]: (s.canvasVideos[String(id)] ?? []).filter((v) => v !== videoId),
  };
  patchState({ canvasVideos });
  return ok({ data: canvasWithCounts(canvas) });
});
